import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
// Components
import {
  Modal,
  Loader,
  ModalTitle,
  PhoneTextField,
  SelectAsync,
  Card,
  MobileTable,
  Table,
  ISelectOption,
  SMBSelect
} from '../../../components';
import { Close, Save, Subject, OtherHouses, DeleteForever, Build, Add } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Fade,
  Button,
  Grid,
  Box,
  useMediaQuery,
  Stack,
  MenuItem,
  Typography,
  Divider
} from '@mui/material';
import { IDomainUser, IResponse } from '../../../models';
import { formatDate } from '../../../helpers';
import {
  IWarrantyUserActiveNeighborhood,
  IWarrantyUserDetail,
  IWarrantyUserNeighborhood
} from '../../../models/warranty-users';
import {
  createWarrantyUser,
  createWarrantyUserNeighborhood,
  deleteWarrantyUserNeighborhood,
  getWarrantyUserAvailableNeighborhoods,
  getWarrantyUserDetail,
  updateWarrantyUser
} from '../../../fetch/warranty-users';
import { getDomainUsers } from '../../../fetch';

interface IWarrantyUserModal {
  open: boolean;
  onClose: () => void;
  warrantyUserId?: string | number;
  fetchWarrantyUsers: () => void;
}

const Schema = Yup.object().shape({
  accountName: Yup.string().max(255, 'Max 255 characters').required('Required'),
  displayName: Yup.string().max(255, 'Max 255 characters').required('Required'),
  contactPhoneNumber: Yup.string().max(255, 'Max 255 characters').required('Required')
});

export const WarrantyUserModal: FC<IWarrantyUserModal> = ({
  open,
  onClose,
  warrantyUserId,
  fetchWarrantyUsers
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');
  const [accountOptions, setAccountOptions] = useState<ISelectOption[] | null>(null);
  const [isDeleting, setDeleting] = useState<{ [id: number]: boolean }>({});

  const {
    isLoading: isLoadingWarrantyUserAvailableNeighborhoods,
    data: warrantyUserNeighborhoods,
    refetch: refetchWarrantyUserNeighborhoods
  } = useQuery<IWarrantyUserActiveNeighborhood[], Error>(
    ['getWarrantyUserAvailableNeighborhoods', warrantyUserId],
    () => getWarrantyUserAvailableNeighborhoods(warrantyUserId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!warrantyUserId
    }
  );

  const {
    isLoading: isLoadingWarrantyUser,
    data: currentWarrantyUser,
    refetch: refetchWarrantyUser
  } = useQuery<IWarrantyUserDetail, Error>(
    ['getWarrantyUserDetail', warrantyUserId],
    () => getWarrantyUserDetail(warrantyUserId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!warrantyUserId
    }
  );

  const handleDelete = async (neighborhoodId: number | string) => {
    const result = window.confirm('Are you sure you want to delete this neighborhood?');
    if (result) {
      setDeleting({ [neighborhoodId]: true });
      try {
        await deleteWarrantyUserNeighborhood(warrantyUserId, neighborhoodId);
        refetchWarrantyUserNeighborhoods();
        refetchWarrantyUser();
        enqueueSnackbar('Neighborhood removed!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Error deleting neighborhood.', {
          variant: 'error'
        });
      } finally {
        setDeleting({ [neighborhoodId]: false });
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Phase',
        accessor: 'phase',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IWarrantyUserNeighborhood } };
        }) => {
          return (
            <>
              <LoadingButton
                loading={isDeleting[original.neighborhoodId]}
                loadingPosition="start"
                color="error"
                disabled={isDeleting[original.neighborhoodId]}
                startIcon={<DeleteForever />}
                onClick={() => {
                  handleDelete(original.neighborhoodId);
                }}
              >
                Delete
              </LoadingButton>
            </>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warrantyUserId, isDeleting]);

  const activeBuildsColumns = useMemo(() => {
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhood',
        sort: false
      },
      {
        Header: 'Lot',
        accessor: 'lot',
        sort: false
      },
      {
        Header: 'Status',
        accessor: 'status',
        sort: false
      },
      {
        Header: 'Frame Date',
        accessor: 'frameDate',
        sort: false,
        Cell: props => <>{formatDate(props.cell.row.original.frameDate)}</>
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        accountName: currentWarrantyUser?.accountName ?? '',
        displayName: currentWarrantyUser?.accountDisplay ?? '',
        contactPhoneNumber: currentWarrantyUser?.contactPhoneNumber ?? ''
      }}
      validationSchema={Schema}
      onSubmit={async (values, actions) => {
        try {
          const data = {
            name: values.accountName,
            displayName: values.displayName,
            contactPhoneNumber: values.contactPhoneNumber
          };
          if (currentWarrantyUser && currentWarrantyUser.warrantyUserId) {
            await updateWarrantyUser(currentWarrantyUser.warrantyUserId, data);
          } else {
            await createWarrantyUser(data);
          }
          enqueueSnackbar(
            currentWarrantyUser ? 'Warranty User Updated!' : 'Warranty User Created!',
            {
              variant: 'success'
            }
          );
          onClose();
          fetchWarrantyUsers();
          actions.resetForm();
        } catch (error) {
          enqueueSnackbar(error?.Detail ?? 'Error saving warranty user.', {
            variant: 'error'
          });
        }
      }}
    >
      {({
        resetForm,
        isSubmitting,
        values,
        errors,
        initialValues,
        handleSubmit,
        dirty,
        isValid,
        setFieldValue
      }) => {
        return (
          <Modal
            open={open}
            onClose={() => {
              if (!deepEqual(initialValues, values)) {
                const result = window.confirm(
                  'You have unsaved changes, are you sure you want to exit?'
                );
                if (result) {
                  resetForm();
                  onClose();
                } else {
                  return;
                }
              } else {
                resetForm();
                onClose();
              }
            }}
            maxWidth="md"
          >
            {(isSubmitting || isLoadingWarrantyUser) && (
              <Loader type="overlay" position="centered" />
            )}
            <Fade in={open}>
              <Form onSubmit={handleSubmit} autoComplete="none">
                <Box mb={1}>
                  <ModalTitle
                    title={!!warrantyUserId ? `Edit Warranty Manager` : 'Add Warranty Manager'}
                  />
                </Box>
                <Card title="Details" icon={<Subject />}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <SelectAsync
                        name="accountName"
                        label="Active Directory User Account"
                        placeholder="Select"
                        required
                        disabled={!!warrantyUserId}
                        apiRequest={() =>
                          getDomainUsers({
                            sortBy: 'Name'
                          })
                        }
                        transformResponse={(response: IResponse<IDomainUser[]>) => {
                          return response.records.map(record => ({
                            label: record.displayName,
                            value: record.accountName
                          }));
                        }}
                        hasClear={!warrantyUserId}
                        onClear={() => {
                          setFieldValue('accountName', '');
                          setFieldValue('displayName', '');
                        }}
                        onChange={e => {
                          const selected = accountOptions?.find(a => a.value === e.target.value);
                          if (selected) {
                            setFieldValue('accountName', selected.value);
                            setFieldValue('displayName', selected.label);
                          }
                        }}
                        handleOptions={options => setAccountOptions(options)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <PhoneTextField
                        fullWidth
                        autoComplete="nope"
                        label="Contact Phone Number"
                        name="contactPhoneNumber"
                        required
                      />
                    </Grid>
                  </Grid>
                  <Box marginTop="2rem">
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={<Save />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      startIcon={<Close />}
                      color="inherit"
                      onClick={() => {
                        if (!deepEqual(initialValues, values)) {
                          const result = window.confirm(
                            'You have unsaved changes, are you sure you want to exit?'
                          );
                          if (result) {
                            resetForm();
                            onClose();
                          } else {
                            return;
                          }
                        } else {
                          onClose();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Card>
                {!!warrantyUserId && (
                  <>
                    <Card title="Neighborhoods" icon={<OtherHouses />}>
                      <Stack
                        gap={1}
                        direction={{
                          xs: 'column',
                          sm: 'row'
                        }}
                        mb={1}
                      >
                        <SMBSelect
                          disabled={isLoadingWarrantyUserAvailableNeighborhoods}
                          label={
                            isLoadingWarrantyUserAvailableNeighborhoods
                              ? 'Loading...'
                              : 'Neighborhood'
                          }
                          value={selectedNeighborhoodId}
                          size="small"
                          onChange={e => setSelectedNeighborhoodId(e.target.value)}
                          handleClear={() => setSelectedNeighborhoodId('')}
                        >
                          {!isLoadingWarrantyUserAvailableNeighborhoods &&
                            warrantyUserNeighborhoods?.length === 0 && (
                              <MenuItem disabled>No neighborhoods available</MenuItem>
                            )}
                          {!isLoadingWarrantyUserAvailableNeighborhoods &&
                            warrantyUserNeighborhoods?.map((record, index) => {
                              return (
                                <MenuItem key={`${index}`} value={record.neighborhoodId}>
                                  {record.name} Phase {record.phase}
                                </MenuItem>
                              );
                            })}
                        </SMBSelect>
                        <Button
                          startIcon={<Add />}
                          sx={{ minWidth: '14rem' }}
                          onClick={async () => {
                            try {
                              await createWarrantyUserNeighborhood(
                                warrantyUserId,
                                selectedNeighborhoodId
                              );
                              refetchWarrantyUserNeighborhoods();
                              refetchWarrantyUser();
                              setSelectedNeighborhoodId('');
                              enqueueSnackbar('Neighborhood added!', {
                                variant: 'success'
                              });
                            } catch (error) {
                              enqueueSnackbar(error?.Detail ?? 'Error creating neighborhood.', {
                                variant: 'error'
                              });
                            }
                          }}
                          color="primary"
                          variant="contained"
                          disabled={!selectedNeighborhoodId}
                        >
                          Add Neighborhood
                        </Button>
                      </Stack>
                      <Divider />
                      {currentWarrantyUser?.neighborhoods?.length === 0 && (
                        <Box mt={1}>
                          <Typography align="center">No neighborhoods found.</Typography>
                        </Box>
                      )}
                      {currentWarrantyUser?.neighborhoods?.length > 0 && (
                        <Table
                          columns={columns}
                          data={currentWarrantyUser?.neighborhoods ?? []}
                          hidePagination
                          mobileProps={{
                            handleDelete: (val: IWarrantyUserNeighborhood) => {
                              handleDelete(val.neighborhoodId);
                            },
                            fields: columns
                              .filter(c => c.Header)
                              .map(c => ({
                                name: c.Header,
                                accessor: c.accessor,
                                Cell: c?.Cell
                              }))
                          }}
                          ResponsiveComponent={isMobile ? MobileTable : undefined}
                        />
                      )}
                    </Card>
                    <Card title="Active Builds" icon={<Build />}>
                      {currentWarrantyUser?.activeBuilds?.length === 0 && (
                        <Typography align="center">No active builds.</Typography>
                      )}
                      {currentWarrantyUser?.activeBuilds?.length > 0 && (
                        <Table
                          columns={activeBuildsColumns}
                          data={currentWarrantyUser?.activeBuilds ?? []}
                          hidePagination
                          mobileProps={{
                            fields: columns
                              .filter(c => c.Header)
                              .map(c => ({
                                name: c.Header,
                                accessor: c.accessor,
                                Cell: c?.Cell
                              }))
                          }}
                          ResponsiveComponent={isMobile ? MobileTable : undefined}
                        />
                      )}
                    </Card>
                  </>
                )}
              </Form>
            </Fade>
          </Modal>
        );
      }}
    </Formik>
  );
};
